import { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useUser from 'hooks/useUser';
import FullLoader from 'components/Loader/FullLoader';
import {
  ADMIN_INITIAL_SETUP_PATH,
  ADMIN_INITIAL_POST_SETUP_PATH,
  ADMIN_ONBOARDING_PENDING_PATH,
  ONBOARDING_SKIPPED_WIZARD_EXIT_LOCATION_STATE,
} from 'pages/Onboarding/constants/constants';
import { useTenantOnboardingSkippedQuery } from 'pages/Onboarding/api/queries';
import { isTenantOnboardingComplete } from 'utils/isTenantOnboardingComplete';
import {
  canAccessMainAppDespiteIncompleteOnboarding,
  shouldForceSuperUserOnboarding,
} from 'utils/module/Onboarding/tenant';

interface OnboardingGuardProps {
  children: ReactNode;
  requireCompleted: boolean;
}

/**
 * Gates a route on tenant onboarding state. Reads `is_onboarding_completed` and
 * `is_onboarding_skipped` from `useUser().currentTenant`.
 *
 * Wrapped around two route kinds in `Router`:
 *   - `requireCompleted = true`  → main app (`path="*"`). Tenant must be completed OR skipped.
 *   - `requireCompleted = false` → wizard route (`/onboarding-setup`). Only super-users with
 *                                  an incomplete, not-skipped tenant belong here.
 *
 * Decision table (main app):
 *   completed | skipped | user        → result
 *   true      | -       | anyone      → render
 *   false     | true    | anyone      → render
 *   false     | false   | super-user  → /onboarding-setup
 *   false     | false   | non-super   → /onboarding-pending
 *
 * Handoff loader: after the wizard completes/skips it navigates to `/` with
 * `onboardingJustCompleted` / `onboardingJustSkipped` in `location.state`. If the tenant cache
 * hasn't refetched yet we'd flicker back to the wizard, so we render `FullLoader` until the
 * tenant context reflects the new flag. `useTenantOnboardingSkippedQuery` is enabled only during
 * that handoff to give us a second source of truth for `is_onboarding_skipped`.
 */
export const OnboardingGuard: FC<OnboardingGuardProps> = ({
  children,
  requireCompleted,
}) => {
  const { user, currentTenant } = useUser();
  const location = useLocation();

  const currentPath = location.pathname.replace(/\/$/, '');
  const onboardingPath = ADMIN_INITIAL_SETUP_PATH.replace(/\/$/, '');
  const isOnboardingPath = currentPath === onboardingPath;

  const tenantIdStr =
    currentTenant?.id != null ? String(currentTenant.id) : undefined;

  const skipHandoffState = location.state as {
    onboardingJustSkipped?: boolean;
    onboardingSkippedWizardExit?: boolean;
  } | null;

  // Refresh `is_onboarding_skipped` only during a wizard↔main-app handoff,
  // where the tenant cache may briefly lag behind the PATCH.
  const shouldFetchTenantSkippedStatus =
    Boolean(user?.id) &&
    currentTenant != null &&
    !isTenantOnboardingComplete(currentTenant) &&
    ((requireCompleted === false && isOnboardingPath) ||
      (requireCompleted === true &&
        (skipHandoffState?.onboardingJustSkipped === true ||
          skipHandoffState?.onboardingSkippedWizardExit === true)));

  const { data: tenantSkippedApiResponse } = useTenantOnboardingSkippedQuery({
    tenantId: tenantIdStr,
    options: {
      enabled: shouldFetchTenantSkippedStatus,
    },
  });

  // Wait until `useAuth` has populated user + tenant; avoids deciding on stale state.
  if (!user?.id || !currentTenant) {
    return <FullLoader id="onboarding-guard-loader" />;
  }

  const isCompleted = isTenantOnboardingComplete(currentTenant);
  const isSuperUser = Boolean(user.super_user);

  // ── Main app branch: tenant is not yet completed. Decide whether to let them through.
  if (requireCompleted && !isCompleted) {
    const locationState = location.state as {
      onboardingJustCompleted?: boolean;
      onboardingJustSkipped?: boolean;
      onboardingSkippedWizardExit?: boolean;
    } | null;

    const skipHandoffActive =
      locationState?.onboardingJustSkipped === true ||
      locationState?.onboardingSkippedWizardExit === true;

    const skippedKnownFromApi =
      skipHandoffActive &&
      tenantSkippedApiResponse?.is_onboarding_skipped === true;

    // Skipped tenant (per context OR per fresh API) → allow the main app.
    if (
      canAccessMainAppDespiteIncompleteOnboarding(currentTenant) ||
      skippedKnownFromApi
    ) {
      return <>{children}</>;
    }

    const awaitingSkippedHandoffOnTenant =
      !canAccessMainAppDespiteIncompleteOnboarding(currentTenant) &&
      !skippedKnownFromApi &&
      skipHandoffActive;

    const awaitingMainAppHandoff =
      isSuperUser &&
      (locationState?.onboardingJustCompleted === true ||
        awaitingSkippedHandoffOnTenant);

    // Just completed/skipped — tenant cache hasn't caught up yet. Show loader, not the wizard.
    if (awaitingMainAppHandoff) {
      return <FullLoader id="onboarding-guard-loader" />;
    }

    if (isSuperUser && !shouldForceSuperUserOnboarding(currentTenant)) {
      return <>{children}</>;
    }

    // Incomplete + not skipped → super-user goes to wizard, others to the pending screen.
    if (isSuperUser) {
      return <Navigate to={ADMIN_INITIAL_SETUP_PATH} replace />;
    }
    return <Navigate to={ADMIN_ONBOARDING_PENDING_PATH} replace />;
  }

  // ── Wizard branch: route is `/onboarding-setup`. Send done users back to the app.
  if (!requireCompleted && isCompleted && isOnboardingPath) {
    return <Navigate to={ADMIN_INITIAL_POST_SETUP_PATH} replace />;
  }

  const skippedEnoughForMainApp =
    canAccessMainAppDespiteIncompleteOnboarding(currentTenant) ||
    tenantSkippedApiResponse?.is_onboarding_skipped === true;

  // Skipped tenants don't belong on the wizard route unless the sidebar progress card
  // navigated here with `resumeOnboardingWizard` to let the super-user finish setup.
  if (
    !requireCompleted &&
    isOnboardingPath &&
    !isCompleted &&
    skippedEnoughForMainApp
  ) {
    const resumeWizard = (
      location.state as { resumeOnboardingWizard?: boolean } | null
    )?.resumeOnboardingWizard;
    if (resumeWizard !== true) {
      return (
        <Navigate
          to={ADMIN_INITIAL_POST_SETUP_PATH}
          replace
          state={ONBOARDING_SKIPPED_WIZARD_EXIT_LOCATION_STATE}
        />
      );
    }
  }

  // Non-super users can never see the wizard.
  if (!requireCompleted && !isCompleted && !isSuperUser && isOnboardingPath) {
    return <Navigate to={ADMIN_ONBOARDING_PENDING_PATH} replace />;
  }

  return <>{children}</>;
};
